import { useEffect, useState, useCallback } from 'react';
import { Loader2, ExternalLink, Plus, Link2, MoreVertical, Unlink, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import type { WorkgroupBoardCount, WorkgroupLinkableBoard } from '@easyflow/shared';
import type { Workgroup } from './types';

interface Props {
  workgroup: Workgroup;
  canManage: boolean;
}

function toastError(err: unknown) {
  const msg = err instanceof Error ? err.message : 'Operazione non riuscita';
  // eslint-disable-next-line no-alert -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)
  alert(msg);
}

export default function TasksTab({ workgroup, canManage }: Props) {
  const [counts, setCounts] = useState<WorkgroupBoardCount | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkable, setLinkable] = useState<WorkgroupLinkableBoard[] | null>(null);
  const [linkableLoading, setLinkableLoading] = useState(false);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<WorkgroupBoardCount>(`/messenger/workgroups/${workgroup.id}/board-count`);
      setCounts(res.data ?? null);
    } catch {
      setCounts(null);
    } finally {
      setLoading(false);
    }
  }, [workgroup.id]);

  useEffect(() => { load(); }, [load]);

  async function handleCreateBoard() {
    setCreating(true);
    try {
      await apiClient.post(`/messenger/workgroups/${workgroup.id}/create-board`, {});
      await load();
    } catch (e) {
      toastError(e);
    } finally {
      setCreating(false);
    }
  }

  async function openLinkDialog() {
    setLinkDialogOpen(true);
    setLinkableLoading(true);
    try {
      const res = await apiClient.get<WorkgroupLinkableBoard[]>(`/messenger/workgroups/${workgroup.id}/linkable-boards`);
      setLinkable(res.data ?? []);
    } catch (e) {
      toastError(e);
      setLinkable([]);
    } finally {
      setLinkableLoading(false);
    }
  }

  async function handleLink(boardId: string) {
    setLinkingId(boardId);
    try {
      await apiClient.post(`/messenger/workgroups/${workgroup.id}/link-board`, { board_id: boardId });
      setLinkDialogOpen(false);
      setLinkable(null);
      await load();
    } catch (e) {
      toastError(e);
    } finally {
      setLinkingId(null);
    }
  }

  async function handleUnlink() {
    if (!confirm('Scollegare la board da questo gruppo? La board resterà disponibile nel modulo Tasks.')) return;
    setUnlinking(true);
    setMenuOpen(false);
    try {
      await apiClient.delete(`/messenger/workgroups/${workgroup.id}/board-link`);
      await load();
    } catch (e) {
      toastError(e);
    } finally {
      setUnlinking(false);
    }
  }

  function openBoard() {
    if (counts?.board_id) {
      const tenantSlug = window.location.pathname.split('/')[1] || '';
      navigate(`/${tenantSlug}/tasks?board_id=${counts.board_id}`);
    }
  }

  if (loading) return <div className="p-8 text-center text-muted-foreground"><Loader2 className="animate-spin inline" /></div>;

  if (!counts?.board_id) {
    return (
      <>
        <div className="p-8 text-center">
          <p className="text-muted-foreground mb-4">Nessuna board tasks collegata.</p>
          {canManage && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={handleCreateBoard}
                disabled={creating}
                className="px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 rounded-lg text-primary-foreground text-sm flex items-center gap-2"
              >
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Crea board
              </button>
              <button
                onClick={openLinkDialog}
                className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-foreground text-sm flex items-center gap-2 border border-border"
              >
                <Link2 size={14} />
                Collega board esistente
              </button>
            </div>
          )}
        </div>
        {linkDialogOpen && (
          <LinkBoardDialog
            boards={linkable}
            loading={linkableLoading}
            linkingId={linkingId}
            onPick={handleLink}
            onClose={() => { setLinkDialogOpen(false); setLinkable(null); }}
            onCreateInstead={() => { setLinkDialogOpen(false); handleCreateBoard(); }}
          />
        )}
      </>
    );
  }

  const pct = counts.total > 0 ? Math.round((counts.completed / counts.total) * 100) : 0;

  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Totale</div>
          <div className="text-2xl font-bold text-foreground">{counts.total}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-emerald-400">Completati</div>
          <div className="text-2xl font-bold text-foreground">{counts.completed}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-blue-400">In corso</div>
          <div className="text-2xl font-bold text-foreground">{counts.in_progress}</div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-foreground/80">Progresso</span>
          <span className="text-sm font-semibold text-foreground">{pct}%</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={openBoard}
          className="flex-1 px-4 py-3 bg-primary hover:bg-primary/90 rounded-xl text-primary-foreground font-medium flex items-center justify-center gap-2"
        >
          Apri board completa <ExternalLink size={16} />
        </button>
        {canManage && (
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              disabled={unlinking}
              className="px-3 py-3 bg-muted hover:bg-muted/80 disabled:opacity-50 rounded-xl text-foreground border border-border"
              aria-label="Altre azioni"
            >
              {unlinking ? <Loader2 size={16} className="animate-spin" /> : <MoreVertical size={16} />}
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-1 w-56 bg-popover border border-border rounded-lg shadow-lg z-20 overflow-hidden">
                  <button
                    onClick={handleUnlink}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2 text-foreground"
                  >
                    <Unlink size={14} /> Scollega board
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface LinkDialogProps {
  boards: WorkgroupLinkableBoard[] | null;
  loading: boolean;
  linkingId: string | null;
  onPick: (boardId: string) => void;
  onClose: () => void;
  onCreateInstead: () => void;
}

function LinkBoardDialog({ boards, loading, linkingId, onPick, onClose, onCreateInstead }: LinkDialogProps) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-foreground">Collega board esistente</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-8 text-center text-muted-foreground">
              <Loader2 className="animate-spin inline" />
            </div>
          )}
          {!loading && boards && boards.length === 0 && (
            <div className="p-8 text-center space-y-3">
              <p className="text-muted-foreground text-sm">Nessuna board libera disponibile.</p>
              <button
                onClick={onCreateInstead}
                className="px-4 py-2 bg-primary hover:bg-primary/90 rounded-lg text-primary-foreground text-sm inline-flex items-center gap-2"
              >
                <Plus size={14} /> Crea una nuova board
              </button>
            </div>
          )}
          {!loading && boards && boards.length > 0 && (
            <ul className="divide-y divide-border">
              {boards.map((b) => (
                <li key={b.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-muted/50">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: b.color || '#6366f1' }}
                      />
                      <span className="font-medium text-foreground truncate">{b.name}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{b.board_type}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {b.task_count} task{b.task_count === 1 ? '' : 's'}
                      {b.description ? ` · ${b.description}` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => onPick(b.id)}
                    disabled={linkingId !== null}
                    className="px-3 py-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 rounded-lg text-primary-foreground text-sm flex items-center gap-2 shrink-0"
                  >
                    {linkingId === b.id ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                    Collega
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
